/**
 * DigiWedge Shared Motion/Animation Tokens
 * Consistent animation durations and easing curves.
 * Aligned with Material Design 3 motion guidelines.
 */
// =============================================================================
// Animation Durations (in milliseconds)
// Aligned with MD3: short=100, medium=250-300, long=500+
// =============================================================================
export const duration = {
    /** 100ms - Instant feedback (button press, hover states) */
    instant: 100,
    /** 150ms - Quick transitions (tooltips, small elements) */
    fast: 150,
    /** 250ms - Standard transitions (most UI interactions) */
    normal: 250,
    /** 350ms - Deliberate transitions (modals, page changes) */
    slow: 350,
    /** 500ms - Complex animations (onboarding, celebrations) */
    slower: 500,
};
// =============================================================================
// CSS Duration Variables
// =============================================================================
export const durationCss = {
    instant: '100ms',
    fast: '150ms',
    normal: '250ms',
    slow: '350ms',
    slower: '500ms',
};
// =============================================================================
// Easing Curves
// =============================================================================
export const easing = {
    /** Standard easing for most animations */
    default: 'ease-out',
    /** Entering elements (modals, dropdowns appearing) */
    easeOut: 'ease-out',
    /** Exiting elements (modals closing, elements disappearing) */
    easeIn: 'ease-in',
    /** Elements that move on screen (repositioning, sliding) */
    easeInOut: 'ease-in-out',
    /** Linear for progress indicators and continuous animations */
    linear: 'linear',
    /** Custom spring-like easing for playful interactions */
    bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    /** Smooth deceleration */
    decelerate: 'cubic-bezier(0, 0, 0.2, 1)',
    /** Smooth acceleration */
    accelerate: 'cubic-bezier(0.4, 0, 1, 1)',
};
// =============================================================================
// React Native Animation Config (for Reanimated)
// =============================================================================
export const springConfig = {
    /** Gentle spring for subtle movements */
    gentle: {
        damping: 20,
        stiffness: 150,
        mass: 1,
    },
    /** Responsive spring for interactive elements */
    responsive: {
        damping: 15,
        stiffness: 200,
        mass: 0.8,
    },
    /** Bouncy spring for playful interactions */
    bouncy: {
        damping: 10,
        stiffness: 180,
        mass: 1,
    },
    /** Stiff spring for quick snaps */
    stiff: {
        damping: 20,
        stiffness: 300,
        mass: 0.8,
    },
};
// =============================================================================
// Animation Delays (for staggered and sequenced animations)
// =============================================================================
export const delays = {
    /** Delay between staggered list items */
    stagger: 50,
    /** Delay for cascading effects */
    cascade: 100,
    /** Delay after component mount before animating */
    afterMount: 300,
    /** Short pause between sequential animations */
    sequence: 150,
    /** Delay before showing tooltip/popover */
    tooltip: 500,
    /** Delay before auto-dismissing toast */
    toastDismiss: 3000,
};
// =============================================================================
// CSS Keyframe Presets (for web)
// =============================================================================
export const keyframes = {
    fadeIn: {
        from: { opacity: 0 },
        to: { opacity: 1 },
    },
    fadeOut: {
        from: { opacity: 1 },
        to: { opacity: 0 },
    },
    scaleIn: {
        from: { opacity: 0, transform: 'scale(0.95)' },
        to: { opacity: 1, transform: 'scale(1)' },
    },
    scaleOut: {
        from: { opacity: 1, transform: 'scale(1)' },
        to: { opacity: 0, transform: 'scale(0.95)' },
    },
    slideInUp: {
        from: { opacity: 0, transform: 'translateY(10px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
    },
    slideInDown: {
        from: { opacity: 0, transform: 'translateY(-10px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
    },
    slideOutUp: {
        from: { opacity: 1, transform: 'translateY(0)' },
        to: { opacity: 0, transform: 'translateY(-10px)' },
    },
    slideOutDown: {
        from: { opacity: 1, transform: 'translateY(0)' },
        to: { opacity: 0, transform: 'translateY(10px)' },
    },
};
//# sourceMappingURL=motion.js.map